/* A FOLDER OF PHOTOGRAPHS, ARRIVING AS ONE FILE.
 *
 * Reported from the thing this site is for: slides photographed at a
 * conference, selected in Google Photos, handed back as a zip, and refused at
 * the drop zone with "Sorry. Doesn't look like a valid IMAGE file." The page's
 * own headline is "Doing a folder of photos one at a time is the worst part of
 * this job" -- and a zip is precisely how every phone gallery, every desktop
 * and every share sheet hands you a folder.
 *
 * It is EXPANDED BEFORE IT IS INTAKE, the way a clip, an SVG and a HEIC
 * already are: the pictures re-enter addFiles() as ordinary files, so the
 * cap, the ordering, the HEIC decode, the previews, the results table and the
 * output zip all apply with no knowledge that a zip was ever involved. There
 * is no flag a caller can set and nothing new in the policy table beyond
 * admitting the archive itself -- tests/file_intake_test.mjs holds that a way
 * to bypass the intake policy is not something to keep, and this does not
 * need one.
 *
 * JSZip is already bundled (compositions/utils.js writes the OUTPUT zip with
 * it), so reading one costs nothing new in the payload.
 */
import * as JSZip from "jszip"

/* WHAT COMES OUT IS JUDGED BY EXTENSION, because an entry in a zip has no
 * type at all -- there is no operating system in the middle to consult its
 * own map. That is the same hole namedByAttr() in lib/task.js exists for,
 * arriving by a different route and in its strongest form: not "absent often
 * enough to matter" but absent always. So the type is RECONSTRUCTED here and
 * written onto the File, rather than left for the intake test to miss.
 *
 * Only the formats the site actually takes. Anything else in the archive is
 * left behind rather than handed on to be refused one step later.
 */
const TYPE_BY_EXT = {
    jpg: "image/jpeg", jpeg: "image/jpeg", jpe: "image/jpeg",
    png: "image/png",
    webp: "image/webp",
    avif: "image/avif",
    jxl: "image/jxl",
    gif: "image/gif",
    heic: "image/heic", heif: "image/heif",
    svg: "image/svg+xml",
    bmp: "image/bmp",
    tif: "image/tiff", tiff: "image/tiff",
    pdf: "application/pdf",
}

/* The upload ceiling, which lives in server.py and is restated rather than
 * imported -- there is no client constant for it today. An entry claiming to
 * be larger than this is skipped BEFORE it is decompressed, which is also the
 * whole of the bomb guard: with at most `want` entries extracted and each one
 * bounded here, a zip can cost this tab exactly what dropping the same files
 * by hand already costs it, and no more. */
export const MAX_ENTRY_BYTES = 52428800

/* Is this file an archive we should try to open?
 *
 * By type AND by name, for the reason looksLikeHeic() gives: Windows reports
 * `application/x-zip-compressed`, some machines report nothing at all, and a
 * zip arriving from a share or another zip has no type either.
 *
 * Deliberately NOT by magic bytes here. This answers "should we try", and the
 * try itself reads the bytes -- a zip whose header says otherwise fails in
 * expandZip() with a sentence, which is better than being told it is not an
 * image.
 */
export function looksLikeZip(file) {
    if (!file) return false
    const type = (file.type || "").toLowerCase()
    if (type === "application/zip" || type === "application/x-zip-compressed" ||
        type === "multipart/x-zip" || type === "application/zip-compressed") return true
    return /\.zip$/i.test(file.name || "")
}

const extensionOf = (name) => {
    const dot = String(name || "").lastIndexOf(".")
    return dot > 0 ? name.slice(dot + 1).toLowerCase() : ""
}

/* ENTRIES THAT ARE NOT PICTURES, and three kinds of them are not obvious.
 *
 * A Google Photos export carries a JSON sidecar per photograph
 * (IMG_0001.jpg.json) and sometimes an album manifest -- those fall out on
 * the extension.
 *
 * A zip made on a Mac carries a parallel __MACOSX tree whose members are
 * named `._IMG_0001.jpg`: they END in .jpg, they are AppleDouble resource
 * forks, and nothing in them decodes. Without this they would double the
 * count, fill the cap with rubbish and push the real photographs out.
 *
 * And a nested .zip is left where it is. One level only -- recursing is how
 * an archive of archives becomes an unbounded amount of work, and nobody
 * sends conference slides that way.
 */
const isJunk = (path) => {
    if (path.startsWith("__MACOSX/") || path.includes("/__MACOSX/")) return true
    const base = path.slice(path.lastIndexOf("/") + 1)
    if (!base || base.startsWith("._") || base === ".DS_Store" || base === "Thumbs.db") return true
    return false
}

/* Two photographs called IMG_0001.jpg from two different albums is not
 * unusual, and the path is flattened to its basename here -- so the names can
 * collide where the archive's did not.
 *
 * A suffix rather than the full path, because the name is what the visitor
 * sees in the results table and what comes back in the output zip, and
 * `Album 2/IMG_0001.jpg` is not a filename anybody wants handed back.
 *
 * downloadBatchZip() in compositions/utils.js has a de-duplicator of its own
 * and this is deliberately not shared with it: that one keeps an OUTPUT
 * archive from silently losing a member, this one names an INPUT, and the two
 * never have to agree on an answer. (Contrast cropRegion(), which is exported
 * and shared precisely because two answers to "which pixels did they pick"
 * would be two different crops.)
 */
function namer() {
    const used = new Set()
    return (name) => {
        if (!used.has(name)) { used.add(name); return name }
        const dot = name.lastIndexOf(".")
        const stem = dot > 0 ? name.slice(0, dot) : name
        const ext = dot > 0 ? name.slice(dot) : ""
        let n = 2
        while (used.has(`${stem}_${n}${ext}`)) n++
        const made = `${stem}_${n}${ext}`
        used.add(made)
        return made
    }
}

/* What is in there, in the order the archive lists it.
 *
 * Separated from the extraction because the COUNT has to be honest while the
 * WORK stays bounded: a sixty-picture zip on a twenty-file page should say
 * sixty and decompress twenty. Listing is the central directory and costs
 * nothing; `async("blob")` is what actually inflates.
 */
export function imageEntries(zip) {
    const found = []
    zip.forEach((path, entry) => {
        if (entry.dir) return
        if (isJunk(path)) return
        const type = TYPE_BY_EXT[extensionOf(path)]
        if (!type) return
        found.push({path, entry, type})
    })
    return found
}

/* Open an archive and hand back the pictures in it.
 *
 * `want` is how many the page can actually use -- batchCapacity() at the call
 * site. Only that many are inflated, so the cost of opening a zip is the cost
 * of the files the page was going to take anyway.
 *
 * Returns {files, found, skipped}: `found` is how many pictures the archive
 * holds, which is what the "that is the most this can do at once" sentence
 * has to quote. Reporting `files.length` there would say twenty out of
 * twenty and mean nothing.
 *
 * Throws "not-an-archive" when the bytes are not a zip, and "no-images" when
 * it opened and held none -- two different things to say, and "doesn't look
 * like a valid IMAGE file" was never either of them.
 */
export async function expandZip(file, want = 20) {
    let zip
    try {
        zip = await JSZip.loadAsync(file)
    } catch (err) {
        throw new Error("not-an-archive")
    }

    const found = imageEntries(zip)
    if (!found.length) throw new Error("no-images")

    const name = namer()
    const files = []
    let skipped = 0

    for (const {path, entry, type} of found) {
        if (files.length >= want) break
        /* `uint8array` RATHER THAN `blob`, and it is not a detail.
         *
         * File takes a BufferSource in its parts list just as happily as a
         * Blob, so the round trip buys nothing in the browser -- and it is
         * what lets tests/zip_intake_test.mjs drive this module in node at
         * all, since JSZip decides whether it can make a Blob once at import
         * time and node 16 has no global one. A module that can only be
         * exercised in a browser is a module whose edge cases are checked by
         * a browser suite competing for a virtual-time budget. */
        let bytes
        try {
            bytes = await entry.async("uint8array")
        } catch (err) {
            skipped++
            continue
        }
        /* Checked after inflating rather than before, because JSZip does not
         * expose an entry's uncompressed size through its public API. The
         * bound that matters is the one above -- at most `want` entries are
         * ever inflated -- so a hostile archive costs this tab what `want`
         * files already cost it. */
        if (bytes.byteLength > MAX_ENTRY_BYTES || bytes.byteLength === 0) {
            skipped++
            continue
        }
        const base = path.slice(path.lastIndexOf("/") + 1)
        files.push(new File([bytes], name(base), {
            type,
            lastModified: (entry.date && entry.date.getTime()) || Date.now(),
        }))
    }

    /* NO THROW WHEN `files` IS EMPTY, and it used to be here.
     *
     * Two ways that happens and neither is "there are no pictures in it".
     * `want` can be ZERO -- a second archive dropped alongside one that
     * already filled the cap -- in which case this holds pictures, they are
     * simply not needed, and its `found` must still be COUNTED so the cap
     * sentence quotes the true total. And every entry can have been skipped
     * for size, which is a different thing to say and the caller says it.
     *
     * Caught by review rather than by a test, then pinned by two: dropping
     * two zips on one page told the visitor the second was empty, and
     * abandoned the whole intake to say it.
     *
     * The only "no-images" is the one above, where the archive opened and
     * really did hold nothing we can use. */
    return {files, found: found.length, skipped}
}
